import { useState } from "react";
import { Building2, Check, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const benefits = [
  "Exclusive district/regional rights",
  "Comprehensive training and support",
  "Marketing materials and branding",
  "Revenue sharing on all programs",
  "Access to all platform resources",
  "Direct support from central team"
];

const indianStates = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Delhi", "Chandigarh", "Jammu and Kashmir", "Ladakh"
];

export default function FranchisePage() {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    district: "",
    state: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/franchise`, formData);
      toast.success("Franchise application submitted! Our team will contact you soon.");
      setFormData({ name: "", email: "", phone: "", district: "", state: "" });
    } catch (error) {
      console.error("Franchise application error:", error);
      toast.error("Failed to submit application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 bg-white" data-testid="franchise-page">
      {/* Hero */}
      <section className="py-16 lg:py-20 bg-gradient-to-br from-forest-950 to-earth-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Building2 className="h-4 w-4" />
            Franchise Opportunity
          </div>
          <h1 className="font-montserrat text-4xl sm:text-5xl font-bold mb-4">
            Become a Carbon Meridian Partner
          </h1>
          <p className="text-xl text-forest-200 max-w-2xl mx-auto">
            Bring India's carbon economy to your region. Build a sustainable business 
            while contributing to climate action.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Benefits */}
            <div>
              <h2 className="font-montserrat text-3xl font-bold text-slate-900 mb-6">
                Why Partner With Us?
              </h2>
              <p className="text-slate-600 mb-8 leading-relaxed">
                As a Carbon Meridian Franchise Partner, you'll represent India's leading 
                climate action platform in your region. Help individuals, institutions, 
                and businesses participate in the carbon economy while building a 
                profitable and meaningful business.
              </p>
              
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-forest-100 rounded-full flex items-center justify-center">
                      <Check className="h-4 w-4 text-forest-700" />
                    </div>
                    <span className="text-slate-700">{benefit}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-6 bg-forest-50 rounded-2xl">
                <h3 className="font-semibold text-forest-900 mb-2">What You'll Do:</h3>
                <ul className="space-y-2 text-sm text-forest-800">
                  <li>• Promote Green Programs (GreenStay, GreenTrack, etc.)</li>
                  <li>• Drive environmental compliance services</li>
                  <li>• Support CBAM compliance for local exporters</li>
                  <li>• Drive corporate partnerships</li>
                  <li>• Organize awareness events</li>
                </ul>
              </div>
            </div>

            {/* Application Form */}
            <Card>
              <CardContent className="p-6 lg:p-8">
                <h3 className="font-montserrat text-xl font-bold text-slate-900 mb-6">
                  Apply for Franchise
                </h3>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Your full name"
                      data-testid="franchise-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="your@email.com"
                      data-testid="franchise-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="Your phone number"
                      data-testid="franchise-phone"
                    />
                  </div>
                  <div>
                    <Label htmlFor="district">District *</Label>
                    <Input
                      id="district"
                      required
                      value={formData.district}
                      onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                      placeholder="Your district"
                      data-testid="franchise-district"
                    />
                  </div>
                  <div>
                    <Label htmlFor="state">State *</Label>
                    <select
                      id="state"
                      required
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
                      data-testid="franchise-state"
                    >
                      <option value="">Select your state</option>
                      {indianStates.map((state) => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-forest-950 hover:bg-forest-900"
                    disabled={submitting}
                    data-testid="franchise-submit"
                  >
                    {submitting ? "Submitting..." : "Submit Application"}
                    <Send className="ml-2 h-4 w-4" />
                  </Button>
                  <p className="text-xs text-slate-500 text-center">
                    Our team will review your application and contact you within 3-5 business days.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
